/**
 * CSV Exporter Utility
 *
 * Generic utility for exporting table data to CSV format with proper escaping and formatting.
 */

/** Primitive values that can be exported to CSV */
export type CsvValue = string | number | boolean | null | undefined;

export interface CsvColumn<T = Record<string, unknown>> {
  /** Column header (translated text) */
  header: string;
  /** Function to extract and format the value from the row */
  getValue: (row: T) => CsvValue;
  /** Optional custom formatter for the value */
  format?: (value: CsvValue) => string;
}

export interface CsvExportOptions {
  /** Filename without extension (will add .csv automatically) */
  filename: string;
  /** Whether to add UTF-8 BOM for Excel compatibility */
  addBOM?: boolean;
  /** Date format to append to filename (default: YYYY-MM-DD) */
  addDateToFilename?: boolean;
}

/**
 * Escapes a CSV value by wrapping in quotes if it contains special characters
 */
const escapeCsvValue = (value: string): string => {
  const stringValue = String(value);
  if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
    return `"${stringValue.replaceAll('"', '""')}"`;
  }
  return stringValue;
};

/**
 * Formats a value for CSV export
 */
const formatValue = (value: CsvValue, formatter?: (value: CsvValue) => string): string => {
  if (value === null || value === undefined) {
    return '';
  }

  if (formatter) {
    return formatter(value);
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  return String(value);
};

/**
 * Exports data to CSV file and triggers download
 *
 * @param data - Array of data objects to export
 * @param columns - Column definitions with headers and value extractors
 * @param options - Export options (filename, BOM, etc.)
 *
 * @example
 * ```typescript
 * exportToCsv(
 *   clients,
 *   [
 *     { header: 'Name', getValue: (row) => row.name },
 *     { header: 'Active', getValue: (row) => row.active },
 *     { header: 'Date', getValue: (row) => row.date, format: (val) => formatDate(val) }
 *   ],
 *   { filename: 'clients', addDateToFilename: true }
 * );
 * ```
 */
export const exportToCsv = <T = Record<string, unknown>>(
  data: T[],
  columns: CsvColumn<T>[],
  options: CsvExportOptions,
): void => {
  const { filename, addBOM = true, addDateToFilename = true } = options;

  // Build CSV headers
  const headers = columns.map((col) => col.header);
  const csvRows: string[] = [];
  csvRows.push(headers.join(','));

  // Build CSV rows
  for (const row of data) {
    const values = columns.map((col) => {
      const rawValue = col.getValue(row);
      const formattedValue = formatValue(rawValue, col.format);
      return escapeCsvValue(formattedValue);
    });
    csvRows.push(values.join(','));
  }

  const csvContent = csvRows.join('\n');

  // Create blob with optional BOM for Excel compatibility
  const bom = addBOM ? '\uFEFF' : '';
  const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });

  // Generate filename with optional date
  let finalFilename = filename;
  if (addDateToFilename) {
    const dateStr = new Date().toISOString().split('T')[0];
    finalFilename = `${filename}_${dateStr}`;
  }
  finalFilename += '.csv';

  // Create download link and trigger download
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', finalFilename);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  link.remove();

  // Clean up object URL
  URL.revokeObjectURL(url);
};
